import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Flame, Save, Loader2, X, Plus } from "lucide-react";
import api from "@/lib/api";
import { timeLeft, pctOff } from "@/components/deals/DealCard";

const Row = ({ d, children, tid }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3" data-testid={tid}>
    {d.service.featured_image ? <img src={d.service.featured_image} alt="" className="h-12 w-16 rounded-lg object-cover" /> : <div className="h-12 w-16 rounded-lg bg-slate-100" />}
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-[#071b22] truncate">{d.title}</div>
      <div className="text-xs text-[#252A34]/60 truncate">-{pctOff(d)}% · {d.provider?.name} · {timeLeft(d.ends_at)} · {d.remaining} left</div>
    </div>
    {children}
  </div>
);

const IconBtn = ({ onClick, disabled, children, tid, label }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label} className="h-8 w-8 rounded-full border border-slate-200 inline-flex items-center justify-center text-[#165a71] hover:bg-slate-50 disabled:opacity-30" data-testid={tid}>{children}</button>
);

export default function AdminHotDealsPage() {
  const [all, setAll] = useState(null);
  const [hot, setHot] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/deals/admin/all").then((r) => {
    setAll(r.data.items);
    setHot(r.data.items.filter((d) => d.is_hot).map((d) => d.id));
  });
  useEffect(() => { load(); }, []);
  if (!all) return <div className="flex items-center gap-2 text-[#252A34]/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const byId = Object.fromEntries(all.map((d) => [d.id, d]));
  const move = (i, dir) => setHot((h) => { const n = [...h]; [n[i], n[i + dir]] = [n[i + dir], n[i]]; return n; });
  const save = async () => {
    setSaving(true);
    try { await api.put("/deals/admin/hot", { order: hot }); toast.success("Hot deals updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Could not save"); } finally { setSaving(false); }
  };
  const others = all.filter((d) => !hot.includes(d.id));

  return (
    <div className="max-w-4xl" data-testid="admin-hot-deals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#071b22] inline-flex items-center gap-2"><Flame className="h-5 w-5 text-[#d97757]" /> Hot deals</h1>
          <p className="text-sm text-[#252A34]/65 mt-1 max-w-2xl">Pick and order the deals shown in the homepage “Hot deals” strip. On category pages, your picks show first, followed by other live deals in that category. If nothing is picked, the homepage shows deals ending soonest.</p>
        </div>
        <button onClick={save} disabled={saving} className="rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="hot-deals-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save order
        </button>
      </div>

      <h2 className="mt-8 mb-3 text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Shown in the strip ({hot.length})</h2>
      <div className="space-y-2" data-testid="hot-deals-selected">
        {hot.length === 0 && <div className="text-sm text-[#252A34]/60 rounded-2xl border border-dashed border-slate-300 p-4" data-testid="hot-deals-selected-empty">No deals picked yet.</div>}
        {hot.filter((id) => byId[id]).map((id, i) => (
          <Row key={id} d={byId[id]} tid={`hot-selected-${id}`}>
            <span className="text-xs font-bold text-[#252A34]/50 w-5 text-center">{i + 1}</span>
            <IconBtn onClick={() => move(i, -1)} disabled={i === 0} tid={`hot-up-${id}`} label="Move up"><ArrowUp className="h-4 w-4" /></IconBtn>
            <IconBtn onClick={() => move(i, 1)} disabled={i === hot.length - 1} tid={`hot-down-${id}`} label="Move down"><ArrowDown className="h-4 w-4" /></IconBtn>
            <IconBtn onClick={() => setHot((h) => h.filter((x) => x !== id))} tid={`hot-remove-${id}`} label="Remove"><X className="h-4 w-4" /></IconBtn>
          </Row>
        ))}
      </div>

      <h2 className="mt-8 mb-3 text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Other live deals ({others.length})</h2>
      <div className="space-y-2" data-testid="hot-deals-available">
        {others.map((d) => (
          <Row key={d.id} d={d} tid={`hot-available-${d.id}`}>
            <button onClick={() => setHot((h) => [...h, d.id])} disabled={hot.length >= 24} className="rounded-full border border-[#165a71]/40 text-[#165a71] px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 hover:bg-[#165a71]/5" data-testid={`hot-add-${d.id}`}><Plus className="h-3.5 w-3.5" /> Add</button>
          </Row>
        ))}
      </div>
    </div>
  );
}
